import { useEffect, useRef } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { useInView } from 'framer-motion'
import styled from 'styled-components'

import playStoreWhiteIcon from '@/assets/images/icon_playstore_w.png'
import subTop1 from '@/assets/images/sub-top-1.png'
import subTop2 from '@/assets/images/sub-top-2.png'
import subTop5 from '@/assets/images/sub-top-5.png'
import subTop34 from '@/assets/images/sub-top-34.png'
import subTop67 from '@/assets/images/sub-top-67.png'
import subTop68 from '@/assets/images/sub-top-68.png'
import Footer from '@/shared/components/layout/Footer'
import Header from '@/shared/components/layout/Header'
import Modal from '@/shared/components/ui/Modal/Modal'
import { STORE_URL } from '@/shared/constants'
import { getBrowserEnv } from '@/shared/utils'
import { Nanum_eb_size, Nanum_r_size } from '@/styles/font/NanumSquare'
import { mediaMobile, mediaTablet } from '@/styles/mediaQuery'
import { icoSet } from '@/styles/mixins'

const Layout = () => {
  const { pathname } = useLocation()

  const pageTitleData = pageTitleMeta[pathname.split('/')[1] as keyof typeof pageTitleMeta]

  const appDownRef = useRef(null)
  const isFooterInView = useInView(appDownRef)

  const isWebView = getBrowserEnv() === 'webview'
  const isHiddenInWebView =
    isWebView &&
    (pathname.includes('notice') || pathname.includes('faq') || pathname.includes('terms'))

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])

  return (
    <MainLayoutWrap $isHiddenInWebView={isHiddenInWebView}>
      {!isHiddenInWebView && <Header />}
      <PageLayoutWrap>
        {pageTitleData && (
          <PageTitleWrap $bgImage={pageTitleData.image}>
            <PageTitleSection>
              <PageTitle>{pageTitleData?.title}</PageTitle>
              <PageDescription>{pageTitleData?.description}</PageDescription>
            </PageTitleSection>
          </PageTitleWrap>
        )}
        <Outlet />
        <Modal />
      </PageLayoutWrap>
      {!isHiddenInWebView && (
        <AppDown target="_blank" href={STORE_URL} $visible={!isFooterInView}>
          앱 다운받기
        </AppDown>
      )}
      {!isHiddenInWebView && <Footer ref={appDownRef} />}
    </MainLayoutWrap>
  )
}
export default Layout

const pageTitleMeta = {
  intro: {
    title: '서비스 소개',
    description: '분실폰을 찾기 위한\n다양한 기능을 제공합니다.',
    image: subTop1,
  },
  faq: {
    title: 'FAQ',
    description: '서비스 이용에 대한\n궁금증을 해결해 드립니다.',
    image: subTop2,
  },
  center: {
    title: '고객센터',
    description: '서비스이용에 어려운점이 있나요?\n도와드릴게요.',
    image: subTop5,
  },
  notice: {
    title: '공지사항',
    description: '서비스 관련 공지사항\n알려드립니다.',
    image: subTop34,
  },
  news: {
    title: '새소식',
    description: '휴대폰분실보호\n새소식 전해드립니다.',
    image: subTop34,
  },
  cancel: {
    title: '서비스 해지',
    description: '서비스 해지를 위해서\n본인인증이 필요합니다.',
    image: subTop67,
  },
  refund: {
    title: '서비스 환불',
    description: '서비스 환불을 위해서\n본인인증이 필요합니다.',
    image: subTop68,
  },
  terms: {
    title: '서비스이용약관',
    description: '휴대폰분실보호\n서비스 이용약관을 알려드립니다.',
    image: subTop67,
  },
}

const MainLayoutWrap = styled.div<{ $isHiddenInWebView: boolean }>`
  width: 100%;
  height: 100%;
  min-height: 100vh;
  display: flex;
  flex-flow: column;
  justify-content: space-between;
  position: relative;
  margin-top: 110px;
  background-color: var(--white);

  ${mediaTablet} {
    margin-top: ${({ $isHiddenInWebView }) => ($isHiddenInWebView ? 0 : 60)}px;
  }
`

const PageLayoutWrap = styled.main`
  flex: 1;
`
const PageTitleWrap = styled.section<{ $bgImage: string }>`
  background-image: ${({ $bgImage }) => `url(${String($bgImage)})`};
  padding: 88px 0 120px;
  background-size: cover;
  background-position: center;

  ${mediaTablet} {
    padding-left: 18%;
    padding-right: 18%;
  }

  ${mediaMobile} {
    padding-left: 24px;
    padding-right: 24px;
  }
`
const PageTitleSection = styled.div`
  margin: 0 auto;
  max-width: 1120px;
  color: var(--white);
`
const PageTitle = styled.strong`
  display: inline-block;
  ${Nanum_r_size(20)};
  margin-bottom: 26px;

  ${mediaTablet} {
    font-size: 16px;
  }
`
const PageDescription = styled.h1`
  ${Nanum_eb_size(40)};
  white-space: pre-line;

  ${mediaTablet} {
    font-size: 28px;
    line-height: 1.35;
  }
`
const AppDown = styled.a<{ $visible: boolean }>`
  background-color: var(--black);
  color: var(--white);
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  text-align: center;
  display: none;
  z-index: 5;

  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  transition:
    visibility 0.3s ease-in,
    opacity 0.3s ease-in;

  &:before {
    content: '';
    display: inline-block;
    vertical-align: middle;
    width: 13px;
    height: 14px;
    margin-right: 10px;
    ${icoSet};
    background-image: url(${playStoreWhiteIcon});
  }

  ${mediaTablet} {
    display: block;
    padding: 21px 0;
    ${Nanum_eb_size(13)}
  }
`
